import { createReducer } from '../../store.model';
import { Scorecard } from '../scorecard-next.model';
import {
    createEmpty,
    createEmptyBox,
    createEmptyData,
    datasetKey,
} from '../scorecard.model';

import { createHistoryPosition } from './general/create-history-position';
import { getYourTeamEnabled } from './general/get-yourteam-enabled';
import { navigateScorecardHistoryReducer } from './general/navigate-scorecard-history.reducer';
import {
    ActionType,
    ListData,
    ListDataResult,
    NavigateHistory,
    Reload,
    ReloadResult,
    Reset,
    SelectPeriodType,
    SetUser,
    ToggleScorecardDetail,
    ToggleYourTeamExpanded,
} from './pulse-widget.action';
import { defaultState, ScorecardWidgetState } from './pulse-widget.state';

const { reducer, builder } = createReducer<ActionType, ScorecardWidgetState>(
    defaultState(),
);

builder.add<SetUser>(ActionType.SCORECARD_WIDGET__SET_USER, (state, action) => {
    const nextState: ScorecardWidgetState = {
        ...state,
        user: action.payload.user,
        showScorecardDetail: action.payload.isOnStateConnect
            ? null
            : state.showScorecardDetail,
        yourTeamExpanded: action.payload.isOnStateConnect
            ? false
            : state.yourTeamExpanded,
        authUserId: action.payload.authUserId,
    };

    nextState.rootConfig = null;
    nextState.periodTypes = null;
    nextState.scorecardHistory = {};
    nextState.scorecardDataset = {};
    nextState.selectedPeriodType = null;
    nextState.selectedScorecardHistory = null;
    nextState.selectedScorecard = null;

    return nextState;
});

builder
    .add<Reload>(ActionType.SCORECARD_WIDGET__RELOAD, (state, action) => {
        return {
            ...state,
            pending: {
                ...state.pending,
                reload: true,
            },
        };
    })
    .add<ReloadResult>(
        ActionType.SCORECARD_WIDGET__RELOAD_RESULT,
        (state, action) => {
            const boxes = action.payload.scorecardBoxes || [createEmptyBox()];

            const nextState: ScorecardWidgetState = {
                ...state,
                pending: {
                    ...state.pending,
                    reload: false,
                },
                scorecardHistory: { ...state.scorecardHistory },
                scorecardDataset: { ...state.scorecardDataset },
            };

            // see setTabData()
            // TODO consolidate when main scorecard state no longer uses maps
            boxes.forEach((box) => {
                // this should never fail, but still
                const pt = box.periodTypes.find(
                    (e) => e.periodTypeId === box.initialScorecard.periodType,
                );
                if (!pt) {
                    return;
                }

                const sh = [...box.scorecardHistory, box.initialScorecard].sort(
                    (a, b) =>
                        +a.endDate - +b.endDate ||
                        a.scorecardId - b.scorecardId,
                );
                nextState.scorecardHistory[pt.periodTypeId] = sh;

                const key = datasetKey(
                    pt.periodTypeId,
                    box.initialScorecardData.scorecardId,
                );
                nextState.scorecardDataset[key] = box.initialScorecardData;

                /// ///////

                nextState.rootConfig = nextState.rootConfig || box.rootConfig;
                nextState.periodTypes =
                    nextState.periodTypes || box.periodTypes;

                nextState.showScorecardDetail =
                    state.showScorecardDetail != null
                        ? state.showScorecardDetail
                        : !nextState.rootConfig.scorecardDetail.collapsed;

                // next period type
                nextState.selectedPeriodType =
                    nextState.selectedPeriodType || pt;

                // next history
                nextState.selectedScorecardHistory =
                    nextState.selectedScorecardHistory || sh;

                // next scorecard
                const nextSelSc = {
                    header: box.initialScorecard || createEmpty(),
                    data: box.initialScorecardData || createEmptyData(),
                };

                nextState.selectedScorecard =
                    nextState.selectedScorecard || nextSelSc;
            });

            nextState.historyPos = createHistoryPosition(
                nextState.selectedScorecardHistory,
                nextState.selectedScorecard,
            );

            nextState.yourTeamEnabled = getYourTeamEnabled(
                nextState.selectedScorecard,
            );

            return nextState;
        },
    );

builder
    .add<ListData>(ActionType.SCORECARD_WIDGET__LIST_DATA, (state) => {
        return {
            ...state,
            pending: {
                ...state.pending,
                listData: true,
            },
        };
    })
    .add<ListDataResult>(
        ActionType.SCORECARD_WIDGET__LIST_DATA_RESULT,
        (state, action) => {
            // TODO consolidate when main scorecard state no longer uses maps
            // next dataset
            const dats = [...Object.values(state.scorecardHistory)].reduce(
                (p, c) => [...p, ...c],
                [],
            );
            const nextScs = { ...state.scorecardDataset };
            action.payload.scorecardData.forEach((e) => {
                const sc = dats.find((ee) => ee.scorecardId === e.scorecardId);
                const key = datasetKey(sc.periodType, e.scorecardId);
                nextScs[key] = e;
            });

            return {
                ...state,
                pending: {
                    ...state.pending,
                    listData: false,
                },
                scorecardDataset: nextScs,
            };
        },
    );

builder.add<SelectPeriodType>(
    ActionType.SCORECARD_WIDGET__SELECT_PERIOD_TYPE,
    (state, action) => {
        // next period type
        const nextPt = state.periodTypes.find(
            (e) => e.periodTypeId === action.payload.periodType,
        );

        // next scorecard history
        const nextSh = state.scorecardHistory[nextPt.periodTypeId];

        // next scorecard
        const head = nextSh[nextSh.length - 1];
        const key = datasetKey(nextPt.periodTypeId, head.scorecardId);
        const nextSc: Scorecard = {
            header: head || createEmpty(),
            data: state.scorecardDataset[key] || createEmptyData(),
        };

        const nextHisPos = createHistoryPosition(nextSh, nextSc);

        const nextYtEnabled = getYourTeamEnabled(nextSc);

        return {
            ...state,
            selectedPeriodType: nextPt,
            selectedScorecardHistory: nextSh,
            selectedScorecard: nextSc,
            historyPos: nextHisPos,
            yourTeamEnabled: nextYtEnabled,
        };
    },
);

builder.add<ToggleScorecardDetail>(
    ActionType.SCORECARD_WIDGET__TOGGLE_SCORECARD_DETAIL,
    (state) => {
        return { ...state, showScorecardDetail: !state.showScorecardDetail };
    },
);

builder.add<NavigateHistory>(
    ActionType.SCORECARD_WIDGET__NAVIGATE_HISTORY,
    (state, action) => {
        return navigateScorecardHistoryReducer(
            state,
            action.payload.historyPosNav,
        );
    },
);

builder.add<ToggleYourTeamExpanded>(
    ActionType.SCORECARD_WIDGET__TOGGLE_YOURTEAM_EXPANDED,
    (state) => {
        return { ...state, yourTeamExpanded: !state.yourTeamExpanded };
    },
);

builder.add<Reset>(ActionType.SCORECARD_WIDGET__RESET, () => {
    return defaultState();
});

export const scorecardWidgetReducer = reducer;
